import { 
  collection, 
  query, 
  where, 
  getDocs, 
  serverTimestamp, 
  addDoc 
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { FAQ } from '../types';

export const findFAQ = async (inputText: string): Promise<FAQ | null> => {
  try {
    const q = collection(db, 'faqs');
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    
    const faqs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FAQ));
    
    const lowerInput = inputText.toLowerCase().trim();
    if (!lowerInput) return null;
    
    // 1. Exact question match first
    const exactMatch = faqs.find(f => f.question?.toLowerCase().trim() === lowerInput);
    if (exactMatch) return exactMatch;

    const inputWords = lowerInput.split(/\s+/).filter(w => w.length > 2);

    // 2. Strong keyword matching
    for (const faq of faqs) {
      if (!faq.keywords || faq.keywords.length === 0) continue;
      
      const lowerKeywords = faq.keywords.map(k => k.toLowerCase().trim());
      
      // Check if any keyword is exactly in the input
      for (const keyword of lowerKeywords) {
        if (lowerInput.includes(keyword)) {
          return faq;
        }
      }

      // Check if any word in input matches any keyword exactly
      for (const word of inputWords) {
        if (lowerKeywords.includes(word)) {
          return faq;
        }
      }
    }
    
    return null;
  } catch (error) {
    console.warn('FAQ search fallback (possible permission or connectivity issue):', error);
    return null;
  }
};

/**
 * Saves a new FAQ entry to the database.
 * This is used to "learn" from AI responses.
 */
export const saveFAQ = async (question: string, answer: string, lang: 'en' | 'te' | 'hi') => {
  try {
    // Only save reasonably long answers and non-empty questions
    if (!question.trim() || question.length < 5 || answer.length < 20) return;

    // Avoid saving if it looks like an error message
    const lowerAnswer = answer.toLowerCase();
    if (lowerAnswer.includes('sorry') || lowerAnswer.includes('technical problem') || lowerAnswer.includes('unable to answer')) return;

    // Extract keywords (words > 3 chars)
    const keywords = question.toLowerCase()
      .replace(/[?.,!/()]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 3 && !['what', 'how', 'when', 'where', 'which', 'who', 'whom', 'whose', 'why', 'this', 'that', 'with', 'from', 'about'].includes(w));

    if (keywords.length === 0) return;

    // Check if a similar question already exists to avoid duplicates
    const existing = await findFAQ(question);
    if (existing) {
      // If found but missing current language transition, we could update it
      // For now, let's just skip to keep it simple
      return;
    }

    const faqData: any = {
      question: question.trim(),
      keywords,
      answer_en: lang === 'en' ? answer : '',
      answer_te: lang === 'te' ? answer : '',
      answer_hi: lang === 'hi' ? answer : '',
      is_auto_generated: true,
      created_at: serverTimestamp(),
    };

    await addDoc(collection(db, 'faqs'), faqData);
    console.log('New FAQ saved from AI response');
  } catch (error) {
    console.error('Error saving new FAQ:', error);
  }
};

// Seed function for initial FAQs if none exist
export const seedInitialFAQs = async (userEmail?: string | null) => {
  // Only attempt seeding if we suspect the user is an admin to avoid unnecessary permission errors
  const isAdmin = userEmail?.toLowerCase() === 'prakruthimithra2026@gmail.com';
  if (!isAdmin) return;

  try {
    const q = collection(db, 'faqs');
    let snapshot;
    try {
      snapshot = await getDocs(q);
    } catch (e) {
      // Quietly fail if we don't have permission - it might be a race condition or stale session
      return;
    }
    
    if (snapshot.empty) {
      const initialFAQs: Omit<FAQ, 'id'>[] = [
        {
          keywords: ['pmds', 'pre-monsoon dry sowing', 'pre monsoon dry sowing'],
          question: 'What is PMDS (Pre-Monsoon Dry Sowing)?',
          answer_en: 'PMDS (Pre-Monsoon Dry Sowing) is a technique where seeds are sown in dry soil 15-30 days before the monsoon. The seeds use atmospheric moisture and evening dews to germinate. It ensures early harvest and better soil health.',
          answer_te: 'PMDS (ముందస్తు రుతుపవన పొడి విత్తడం) అనగా రుతుపవనాలు రాకముందే 15-30 రోజుల ముందు పొడి నేలలో విత్తనాలు విత్తే పద్ధతి. విత్తనాలు వాతావరణంలోని తేమ మరియు సాయంత్రం కురిసే మంచును ఉపయోగించి మొలకెత్తుతాయి. ఇది ముందస్తు పంట మరియు మెరుగైన నేల ఆరోగ్యానికి సహాయపడుతుంది.',
          answer_hi: 'PMDS (मानसून पूर्व शुष्क बुवाई) एक ऐसी तकनीक है जिसमें मानसून से 15-30 दिन पहले सूखी मिट्टी में बीज बोए जाते हैं। बीज अंकुरित होने के लिए वायुमंडलीय नमी और शाम की ओस का उपयोग करते हैं। यह जल्दी फसल और बेहतर मिट्टी का स्वास्थ्य सुनिश्चित करता है।'
        },
        {
          keywords: ['npv', 'nuclear polyhedrosis virus', 'nuclear polyhedrosis'],
          question: 'What is NPV and how to use it?',
          answer_en: 'NPV (Nuclear Polyhedrosis Virus) is a highly specific biological pesticide used to control Spodoptera and Helicoverpa larvae. It is safe for humans and environment. Spodoptera NPV for tobacco caterpillar and Helicoverpa NPV for gram pod borer. Use 200 LE/acre.',
          answer_te: 'NPV (న్యూక్లియర్ పాలిహెడ్రోసిస్ వైరస్) అనేది స్పోడొప్టెరా మరియు హెలికోవెర్ప లార్వాలను నియంత్రించడానికి ఉపయోగించే అత్యంత నిర్దిష్ట జీవ పురుగుమందు. ఇది మానవులకు మరియు పర్యావరణానికి సురక్షితమైనది. పొగాకు గొంగళి పురుగు కోసం స్పోడొప్టెరా NPV మరియు శనగ పచ్చ పురుగు కోసం హెలికోవెర్ప NPV ఉపయోగించాలి. ఎకరాకు 200 LE వాడండి.',
          answer_hi: 'NPV (न्यूक्लियर पॉलीहेड्रोसिस वायरस) एक अत्यधिक विशिष्ट जैविक कीटनाशक है जिसका उपयोग स्पोडोप्टेरा और हेलिकोवर्पा लार्वा को नियंत्रित करने के लिए किया जाता है। यह मनुष्यों और पर्यावरण के लिए सुरक्षित है। तंबाकू इल्ली के लिए स्पोडोप्टेरा NPV और चने की फली छेदक के लिए हेलिकोवर्पा NPV का उपयोग करें। 200 LE/एकड़ का उपयोग करें।'
        }
      ];
      
      for (const faq of initialFAQs) {
        await addDoc(collection(db, 'faqs'), faq);
      }
      console.log('FAQs seeded successfully');
    }
  } catch (error) {
    console.error('Error seeding FAQs:', error);
  }
};
